import { React, variantProvider, Theme } from '@/app'
import {
  CenterWrapper,
  Drawer,
  Logo,
  Image,
  Link,
  Text,
  View,
  Icon,
  Touchable,
} from '@/components'
import {
  useCodeleapContext,
  useComponentStyle,
  useBooleanToggle,
  useState,
  useEffect,
} from '@codeleap/common'
import { useLocation, navigate } from '@reach/router'

export type HeaderProps = {
  white?: boolean
}

type NavItemProps = {
  text: string
  link: string
  isSelected?: boolean
  showUnderline?: boolean
  onPress?: () => void
}

const navItems = [
  { text: 'Home', link: '/' },
  { text: 'About', link: '/about' },
  { text: 'Media', link: '/media' },
  { text: 'Contact Us', link: '/contact' },
]

const NavItem = (props: NavItemProps) => {

  const { text, link, isSelected, showUnderline = true, onPress } = props

  const styles = useComponentStyle(componentStyles)

  const handlePress = () => {
    if (onPress) onPress()
    navigate(link)
  }

  if (onPress) {
    return (
      <View variants={['column', 'alignCenter']} responsiveVariants={{ small: ['column', 'alignStart'] }}>
        <Touchable onPress={handlePress}>
          <Text css={[isSelected ? styles.linkRed : styles.linkBrown]} text={`${text}`} />
        </Touchable>
        {isSelected && showUnderline ? <View css={[styles.navItem]} /> : null}
      </View>
    )
  }

  return (
    <View variants={['column', 'alignCenter']} responsiveVariants={{ small: ['column', 'alignStart'] }}>
      <Link css={[isSelected ? styles.linkRed : styles.linkBrown]} text={`${text}`} to={`${link}`} />
      {isSelected && showUnderline ? <View css={[styles.navItem]} /> : null}
    </View>
  )
}

export const Header = (props: HeaderProps) => {
  const styles = useComponentStyle(componentStyles)
  const [drawer, toggleDrawer] = useBooleanToggle(false)

  const location = useLocation()
  const activeScreen = location.pathname

  const { white } = props

  const isHeaderWhite = !!white

  useEffect(() => {
    if (drawer) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [drawer])

  return (
    <>
      <CenterWrapper
        styles={{
          innerWrapper: styles.wrapper,
          wrapper: styles.floatingHeaderBelow,
        }}
      >
        <Link to={'/'} css={styles.logoWrapper}>
          <Logo style={styles.logo} variants={[`${isHeaderWhite ? 'primary' : ''}`]} />
        </Link>
        <Touchable onPress={() => toggleDrawer()} down={'small'}>
          <Icon name={'bars'} style={styles.icon} />
        </Touchable>
        <View variants={['gap:4', 'justifyCenter']} up={'small'}>
          {navItems.map((el, index) => <NavItem key={index} text={el.text} link={el.link} isSelected={el?.link === activeScreen} />)}

        </View>
      </CenterWrapper>
      <Drawer
        open={drawer}
        toggle={toggleDrawer}
        variants={['fullScreen']}
        styles={{
          box: styles.fullHeight,
          wrapper: styles.drawerWrapper,
          // headerCloseButton: styles.drawerCloseButton,
        }}
      // showCloseButton

      >
        <CenterWrapper styles={{ wrapper: styles.drawerContent, innerWrapper: { alignItems: 'flex-start' } }} variants={['column', 'paddingVertical:2']}>
          <View variants={['alignCenter', 'justifySpaceBetween', 'marginBottom:3', 'fullWidth']}>
            <Touchable onPress={() => { toggleDrawer(); navigate('/'); }} css={styles.logoWrapper}>
              <Logo style={styles.logo} variants={['primary']} />
            </Touchable>
            <Touchable onPress={toggleDrawer}>
              <Icon name={'close'} style={styles.drawerCloseButton} />
            </Touchable>
          </View>
          <View variants={['column', 'gap:4']}>
            {navItems.map((el, index) => (
              <NavItem key={index} text={el.text} link={el.link} isSelected={el?.link === activeScreen} showUnderline={false} onPress={toggleDrawer} />
            ))}
          </View>
        </CenterWrapper>
      </Drawer>
    </>
  )
}

const componentStyles = variantProvider.createComponentStyle((theme) => ({
  wrapper: {
    ...theme.presets.row,
    ...theme.spacing.padding(1),
    justifyContent: 'space-between',
    minHeight: '100px',
    [theme.media.down('small')]: {
      minHeight: '60px',
    },
  },
  logo: {
    width: 140,
    [theme.media.down('small')]: {
      width: 120,
    },
  },
  logoWrapper: {
    marginRight: 'auto',
    [theme.media.down('small')]: {
      marginRight: 0,
      display: 'flex',
    },
  },
  fullHeight: {
    height: '100dvh', // dvh = dynamic viewport height, funciona em mobile
    maxHeight: '-webkit-fill-available', // fallback para Safari antigo
    backgroundColor: theme.colors.primary,
  },
  drawerWrapper: {
    height: '100dvh',
    maxHeight: '-webkit-fill-available',
    flex: 1,
    backgroundColor: theme.colors.primary,
    zIndex: 9999999,
  },
  floatingHeaderBelow: {
    position: 'fixed',
    zIndex: 999999,
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.primary,
  },
  drawerContent: {
    alignItems: 'flex-start',
    width: '100%',
    backgroundColor: theme.colors.primary,
  },
  navLink: {
    [theme.media.down('small')]: {
      textDecoration: 'none',
    },
  },
  icon: {
    width: 24,
    height: 24,
    color: theme.colors.primarySolid600,
    stroke: theme.colors.primarySolid600,

    svg: {
      color: theme.colors.primarySolid600,
      strokeColor: theme.colors.primarySolid600,
    },
  },
  iconRed: {
    width: 24,
    height: 24,
    color: 'red',
    stroke: 'red',

    svg: {
      color: 'red',
      strokeColor: 'red',
    },
  },
  linkRed: {
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 1,
    letterSpacing: 0,
    '&:hover': {
      color: theme.colors.primarySolid300,
    },
    color: theme.colors.primarySolid300,
    margin: '0 auto',
    [theme.media.down('small')]: {
      margin: 0,
    },
  },
  linkBrown: {
    color: theme.colors.primarySolid600,
    margin: '0 auto',
    fontWeight: 400,
    fontSize: 18,
    lineHeight: 1,
    letterSpacing: 0,
    '&:hover': {
      color: theme.colors.primarySolid600,
    },
    [theme.media.down('small')]: {
      margin: 0,
    },
  },
  drawerCloseButton: {
    size: 20,
    color: 'red',
  },
  navItem: {
    backgroundColor: theme.colors.primarySolid300,
    width: '130%',
    height: 2,
    borderRadius: 100000,
    marginTop: theme.spacing.value(0.8),
  },
  navItemRed: {
    backgroundColor: theme.colors.primary,
  },
}))
